/// <reference types="google.maps" />

let loader: Promise<typeof google.maps> | null = null;

export const getGoogleMapsKey = () =>
  (import.meta.env.VITE_GOOGLE_MAPS_API_KEY as string | undefined) ?? '';

// Google's official JS Bootstrap Loader. Installs `google.maps.importLibrary`
// synchronously; the actual Maps JS script is fetched lazily the first time a
// library is requested. Translated from the minified snippet in Google's docs:
// https://developers.google.com/maps/documentation/javascript/load-maps-js-api
const bootstrap = (config: { key: string; v: string }) => {
  const PKG = 'The Google Maps JavaScript API';
  const win = window as unknown as { google?: { maps?: Record<string, unknown> } };
  win.google = win.google ?? {};
  const maps = (win.google.maps = win.google.maps ?? {});
  const requested = new Set<string>();
  let scriptPromise: Promise<void> | undefined;

  const loadScript = () =>
    scriptPromise ??
    (scriptPromise = new Promise<void>((res, rej) => {
      const params = new URLSearchParams();
      params.set('libraries', [...requested].join(','));
      for (const [k, v] of Object.entries(config)) {
        params.set(k.replace(/[A-Z]/g, (t) => '_' + t[0]!.toLowerCase()), v);
      }
      params.set('callback', 'google.maps.__ib__');
      const s = document.createElement('script');
      s.src = `https://maps.googleapis.com/maps/api/js?${params}`;
      (maps as { __ib__?: () => void }).__ib__ = () => res();
      s.onerror = () => rej(new Error(`${PKG} could not load.`));
      s.nonce = document.querySelector<HTMLScriptElement>('script[nonce]')?.nonce ?? '';
      document.head.append(s);
    }));

  if (maps.importLibrary) return;
  (maps as { importLibrary: (name: string, ...rest: unknown[]) => Promise<unknown> })
    .importLibrary = (name, ...rest) => (
      requested.add(name),
      loadScript().then(() =>
        (maps as { importLibrary: (n: string, ...r: unknown[]) => Promise<unknown> })
          .importLibrary(name, ...rest),
      )
    );
};

export const loadGoogleMaps = (): Promise<typeof google.maps> => {
  if (typeof window !== 'undefined' && typeof window.google?.maps?.importLibrary === 'function') {
    return Promise.resolve(window.google.maps);
  }
  if (loader) return loader;

  const key = getGoogleMapsKey();
  if (!key) {
    return Promise.reject(new Error('Google Maps API key is not configured'));
  }

  bootstrap({ key, v: 'weekly' });
  loader = Promise.resolve(window.google!.maps);
  return loader;
};
